import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { TypographyWithTooltip } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { BaseButton, Box, Typography } from '@lyearn/ui';
import stopPropagation from '@/consts/stopPropogation';
import FileEntity from '@/modules/tasks/file.entity';
import { svgSizeStyles } from '@/utils/styles';

import { useActionItemContext } from '../../context';
import { ActionStageProps } from '../../types';
import { disabledButtonStyles, linkButtonStyles } from '../ActionItem/styles';
import { isTaskEditable } from '../utils';
import ProjectStageMenu from './ProjectStageMenu';

const ActionStage = ({ size = 18, className }: ActionStageProps) => {
  const { t } = useTranslation(['Task']);
  const {
    actionItem,
    actions,
    provider,
    isReadOnly,
    disableInteractiveTooltip = false,
  } = useActionItemContext();

  const isDisabled = !isTaskEditable(provider);

  return (
    <Box className={cn('w-full min-w-0', className)} onClick={stopPropagation}>
      <ProjectStageMenu
        disabled={isDisabled || isReadOnly || !actions}
        popoverPosition="bottom-start"
        projectId={
          actionItem.file && FileEntity.isFileTypeProject(actionItem.file)
            ? actionItem.fileId ?? undefined
            : undefined
        }
        stageId={actionItem.stageIds?.[0] ?? undefined}
        onStageChange={(stageId) =>
          actions?.onStageChange(stageId ? [stageId] : undefined, actionItem.id)
        }>
        {({ selectedOptions }) => {
          const selectedStage = selectedOptions[0];
          return (
            <BaseButton
              className={cn(
                'px-8 h-32 truncate w-full min-w-0 justify-start gap-6',
                svgSizeStyles(size / 10),
                linkButtonStyles,
                {
                  [disabledButtonStyles]: isDisabled || isReadOnly,
                },
              )}
              disabled={isDisabled || isReadOnly}>
              {selectedStage?.Icon ? <selectedStage.Icon className="shrink-0 p-2" /> : null}
              {selectedStage ? (
                <TypographyWithTooltip
                  className="min-w-0 truncate"
                  disableInteractive={disableInteractiveTooltip}
                  numberOfLines={1}
                  variant="body-short-02">
                  {selectedStage?.label}
                </TypographyWithTooltip>
              ) : (
                <Typography color="text-text-placeholder" variant="body-short-02">
                  -
                </Typography>
              )}
            </BaseButton>
          );
        }}
      </ProjectStageMenu>
    </Box>
  );
};

export default React.memo(ActionStage);
